import { useCallback, useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { FormProvider, useForm } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Card, CircularProgress, Stack, Typography } from "@mui/material"
import { toast } from "react-toastify"
import { FormBlock } from "../../../../../common/FormGenerator"
import { getFormConfig } from "./settingsConfig"
import { imagesSchema } from "../ZodValidationSchema/ImagesSchema"
import {
    useGetProjectImagesQuery,
    useUploadProjectPreviewMutation,
    useUploadProjectGalleryMutation,
    useDeleteProjectGalleryImageMutation,
} from "../../../../../api/projects"
import { CardStyle, TypStyle } from "./style"
import { ImageUploader } from "../../../../../components/ui-kit/ImageUploader"
import { getMediaSourceUrl } from "../../../../../utils/media"

export const EditImagesForm = () => {
    const { id } = useParams<{ id: string }>()
    const projectId = id ? parseInt(id, 10) : NaN
    const {
        data: loaded,
        isLoading,
        isError,
        refetch,
    } = useGetProjectImagesQuery(
        { projectId },
        { skip: Number.isNaN(projectId) }
    )
    const [uploadProjectPreview] = useUploadProjectPreviewMutation()
    const [uploadProjectGallery] = useUploadProjectGalleryMutation()
    const [deleteProjectGalleryImage] = useDeleteProjectGalleryImageMutation()

    const methods = useForm({
        mode: 'onBlur',
        resolver: zodResolver(imagesSchema),
        defaultValues: {
            avatarUrl: null,
            images: [],
        },
    })

    const { reset, watch } = methods
    const galleryInputRef = useRef<HTMLInputElement>(null)
    const [isGalleryBusy, setIsGalleryBusy] = useState(false)
    const imagesCount = Array.isArray(watch('images')) ? watch('images').length : 0

    const handleAvatarCropSaved = useCallback(async (file: File) => {
        if (Number.isNaN(projectId)) {
            throw new Error('invalid project')
        }
        const result = await uploadProjectPreview({ projectId, file }).unwrap()
        const url = getMediaSourceUrl(result)
        if (!url) {
            throw new Error('no url')
        }
        toast.success("Аватарка проекта сохранена")
        return url
    }, [projectId, uploadProjectPreview])

    useEffect(() => {
        if (loaded) {
            const mappedData = {
                avatarUrl: typeof loaded.avatarUrl === 'string' ? loaded.avatarUrl : null,
                images: Array.isArray(loaded.images) ? loaded.images : [],
            }
            reset(mappedData)
        }
    }, [loaded, reset])

    useEffect(() => {
        if (isError) {
            toast.error("Не удалось загрузить изображения проекта")
        }
    }, [isError])

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%">
                <CircularProgress />
            </Stack>
        )
    }
    const statsTextSx = { fontSize: 12, lineHeight: 1.4, textTransform: 'none' as const }

    return (
        <FormProvider {...methods}>
            <Stack gap={2}>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Аватарка проекта
                    </Typography>
                    <FormBlock
                        formFieldConfig={getFormConfig('avatar', {
                            onCropSaved: handleAvatarCropSaved,
                            hideRemove: true,
                        })}
                    />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                    >
                        <Typography variant="black-16-semiBold" component="div">
                            Изображения (максимум 10)
                        </Typography>
                        <Button
                            type="button"
                            variant="action"
                            onClick={() => galleryInputRef.current?.click()}
                            disabled={imagesCount >= 10 || isGalleryBusy}
                        >
                            {isGalleryBusy ? "Загрузка…" : "Добавить"}
                        </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={statsTextSx}>
                        PNG, JPG до 5МБ
                    </Typography>
                    <ImageUploader
                        ref={galleryInputRef}
                        hideDropZone
                        emptyPlaceholder="Добавьте изображения"
                        name="images"
                        onBusyChange={setIsGalleryBusy}
                        onFilesSelected={async (files) => {
                            if (Number.isNaN(projectId)) return { mode: 'replace', images: [] }
                            // One file per request (nginx/PHP body limits). Then reload gallery from GET show —
                            // upload MediaResource historically returned the first collection URL for every item.
                            for (const file of files) {
                                await uploadProjectGallery({
                                    projectId,
                                    files: [file],
                                }).unwrap()
                            }
                            const refreshed = await refetch().unwrap()
                            return {
                                mode: 'replace' as const,
                                images: (refreshed.images ?? [])
                                    .map((item) => ({
                                        mediaId: Number(item.mediaId),
                                        url: item.url,
                                    }))
                                    .filter((item) => Number.isFinite(item.mediaId) && item.mediaId > 0 && Boolean(item.url)),
                            }
                        }}
                        onRemoveImage={async ({ mediaId }) => {
                            if (Number.isNaN(projectId)) return
                            if (!mediaId) return
                            await deleteProjectGalleryImage({ projectId, mediaId }).unwrap()
                        }}
                    />
                </Card>
            </Stack>
        </FormProvider>
    )
}
